import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import invoiceService from '../../../services/invoiceService';
import NotificationToast from '../../../components/ui/NotificationToast';

const BillingAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState({
    totalReceivables: 0,
    overdueAmount: 0,
    paidThisMonth: 0,
    pendingInvoices: 0,
    overdueInvoices: 0,
    averagePaymentDays: 0,
    collectionRate: 0
  });
  const [monthlyTrends, setMonthlyTrends] = useState([]);
  const [paymentStatusData, setPaymentStatusData] = useState([
    { name: 'Paid', value: 0, color: '#10B981' },
    { name: 'Pending', value: 0, color: '#F59E0B' },
    { name: 'Overdue', value: 0, color: '#EF4444' }
  ]);
  const [topClients, setTopClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await invoiceService.getAnalytics();
      
      if (result.success && result.data) {
        const data = result.data;
        setAnalyticsData({
          totalReceivables: data.totalReceivables || 0,
          overdueAmount: data.overdueAmount || 0,
          paidThisMonth: data.paidThisMonth || 0,
          pendingInvoices: data.pendingInvoices || 0,
          overdueInvoices: data.overdueInvoices || 0,
          averagePaymentDays: data.averagePaymentDays || 0,
          collectionRate: data.collectionRate || 0
        });
        
        setMonthlyTrends(data.monthlyTrends || []);
        
        if (data.paymentStatusData) {
          setPaymentStatusData([
            { name: 'Paid', value: data.paymentStatusData.paid || 0, color: '#10B981' },
            { name: 'Pending', value: data.paymentStatusData.pending || 0, color: '#F59E0B' },
            { name: 'Overdue', value: data.paymentStatusData.overdue || 0, color: '#EF4444' }
          ]);
        }
        
        setTopClients(data.topClients || []);
      } else {
        throw new Error(result.error || 'Failed to load analytics');
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.message || 'Failed to load analytics data');
      setNotification({ message: 'Failed to load analytics data', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(1)}Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(1)}K`;
    }
    return `₹${amount}`;
  };

  const getClientStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'text-success';
      case 'good': return 'text-primary';
      case 'average': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground text-sm">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="text-red-600">⚠️</div>
          <div>
            <p className="text-red-800 font-medium">Error loading analytics</p>
            <p className="text-red-600 text-sm">{error}</p>
            <button 
              onClick={loadAnalytics}
              className="text-red-700 underline text-sm mt-1"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onDismiss={() => setNotification(null)}
          position="top-right"
          autoHideDuration={3000}
        />
      )}
      <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={20} className="text-primary" />
            </div>
            {analyticsData.totalReceivables > 0 && (
              <span className="text-xs text-success bg-success/10 px-2 py-1 rounded-full">
                Active
              </span>
            )}
          </div>
          <div className="text-2xl font-bold text-foreground mb-1">
            {formatCompactCurrency(analyticsData.totalReceivables)}
          </div>
          <div className="text-sm text-muted-foreground">Total Receivables</div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
              <Icon name="AlertCircle" size={20} className="text-error" />
            </div>
            {analyticsData.overdueAmount > 0 && (
              <span className="text-xs text-error bg-error/10 px-2 py-1 rounded-full">
                {analyticsData.overdueInvoices} overdue
              </span>
            )}
          </div>
          <div className="text-2xl font-bold text-foreground mb-1">
            {formatCompactCurrency(analyticsData.overdueAmount)}
          </div>
          <div className="text-sm text-muted-foreground">Overdue Amount</div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-success" />
            </div>
            {analyticsData.paidThisMonth > 0 && (
              <span className="text-xs text-success bg-success/10 px-2 py-1 rounded-full">
                This Month
              </span>
            )}
          </div>
          <div className="text-2xl font-bold text-foreground mb-1">
            {formatCompactCurrency(analyticsData.paidThisMonth)}
          </div>
          <div className="text-sm text-muted-foreground">Collected This Month</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trends */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Monthly Trends</h3>
              <p className="text-sm text-muted-foreground">Invoice vs Collection Analysis</p>
            </div>
            <Icon name="TrendingUp" size={20} className="text-muted-foreground" />
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="month" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  tickFormatter={formatCompactCurrency}
                />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), '']}
                  labelStyle={{ color: 'var(--color-foreground)' }}
                  contentStyle={{ 
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="invoiced" 
                  stroke="var(--color-primary)" 
                  strokeWidth={2}
                  name="Invoiced"
                />
                <Line 
                  type="monotone" 
                  dataKey="collected" 
                  stroke="var(--color-success)" 
                  strokeWidth={2}
                  name="Collected"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payment Status Distribution */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Payment Status</h3>
              <p className="text-sm text-muted-foreground">Current Invoice Distribution</p>
            </div>
            <Icon name="PieChart" size={20} className="text-muted-foreground" />
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={paymentStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {paymentStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Percentage']}
                  contentStyle={{ 
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="flex justify-center gap-6 mt-4">
            {paymentStatusData.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm text-muted-foreground">
                  {item.name} ({item.value}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg border border-border p-4 text-center">
          <div className="text-3xl font-bold text-primary mb-2">
            {analyticsData.averagePaymentDays}
          </div>
          <div className="text-sm text-muted-foreground">Avg Payment Days</div>
          {analyticsData.averagePaymentDays > 0 && (
            <div className="text-xs text-muted-foreground mt-1">Average</div>
          )}
        </div>

        <div className="bg-card rounded-lg border border-border p-4 text-center">
          <div className="text-3xl font-bold text-success mb-2">
            {analyticsData.collectionRate}%
          </div>
          <div className="text-sm text-muted-foreground">Collection Rate</div>
          {analyticsData.collectionRate > 0 && (
            <div className="text-xs text-muted-foreground mt-1">Collection efficiency</div>
          )}
        </div>

        <div className="bg-card rounded-lg border border-border p-4 text-center">
          <div className="text-3xl font-bold text-warning mb-2">
            {analyticsData.pendingInvoices}
          </div>
          <div className="text-sm text-muted-foreground">Pending Invoices</div>
          {analyticsData.overdueInvoices > 0 && (
            <div className="text-xs text-error mt-1">{analyticsData.overdueInvoices} overdue</div>
          )}
        </div>
      </div>

      {/* Top Clients */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Top Clients</h3>
              <p className="text-sm text-muted-foreground">By outstanding amount</p>
            </div>
            <Icon name="Users" size={20} className="text-muted-foreground" />
          </div>
        </div>
        
        <div className="p-6">
          {topClients.length > 0 ? (
            <div className="space-y-4">
              {topClients.map((client, index) => (
              <div key={index} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">
                      {client.name.split(' ').map(word => word[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-foreground">{client.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {client.invoices} invoices
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="font-medium text-foreground">
                    {formatCurrency(client.amount)}
                  </div>
                  <div className={`text-sm ${getClientStatusColor(client.status)}`}>
                    {client.status}
                  </div>
                </div>
              </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>No outstanding invoices to display</p>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default BillingAnalytics;